import React, { useEffect, useRef, useState } from "react";
import Logo from "../../../common/Logo";
import { useAuth } from "../../../../contexts/AuthContext";
import NavItem from "../NavItem";
import { LogOut, Layers, X } from "lucide-react";

const VerticalNavbar = ({ disableInternalMobile = false, onNavigate }) => {
  const { logout } = useAuth();
  const [activeMenu, setActiveMenu] = useState("/home");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // centralize nav content so it can be rendered in desktop aside,
  // inside this component's drawer, or directly when embedded in App's overlay
  const NavContent = (
    <div className="flex h-full flex-col">
      <div>
        <div className="px-10 pt-10 pb-9">
          {/* hide this logo on mobile, show on md+ */}
          <div className="hidden md:block">
            <Logo />
          </div>
        </div>
        <nav className="px-6">
          <ul>
            <NavItem
              link="/home"
              icon={
                <Layers size={18} className="size-6" />
              }
              menu="Task Board"
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onSelect={(m) => {
                setActiveMenu(m || "/home");
                if (disableInternalMobile && typeof onNavigate === "function")
                  onNavigate();
                else setMobileOpen(false);
              }}
            />

            <NavItem
              link="#logout"
              icon={
                <LogOut size={18} className="size-6" />
              }
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              onSelect={(m) => {
                setActiveMenu(m || "/home");
                if (disableInternalMobile && typeof onNavigate === "function")
                  onNavigate();
                else setMobileOpen(false);
                logout();
              }}
              menu="Log out"
            />
          </ul>
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile header (visible on small screens) - can be disabled by parent */}
      {!disableInternalMobile && (
        <header className="md:hidden fixed left-0 right-0 top-0 z-40 bg-white border-b">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                aria-label="Open menu"
                onClick={() => setMobileOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <Layers size={20} />
              </button>
              {/* Hide logo on small screens; show on md+ */}
              <div className="hidden md:block h-6">
                <Logo />
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Desktop aside */}
      <aside className="hidden md:block h-screen bg-white">
        <div className="shadow-card bg-gray-2 flex h-screen w-full max-w-[300px] flex-col justify-between overflow-y-auto">
          {NavContent}
        </div>
      </aside>

      {/* when VerticalNavbar is embedded into App overlay, render the nav content directly */}
      {disableInternalMobile && (
        <div className="p-4 h-full overflow-y-auto bg-white">{NavContent}</div>
      )}

      {/* Mobile drawer - can be disabled by parent */}
      {!disableInternalMobile && mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute left-0 top-0 bottom-0 w-[260px] bg-white shadow-lg overflow-y-auto">
            <div className="px-4 pt-6 pb-4">
              <div className="flex items-center justify-between hidden md:block">
                {/* Hide logo inside mobile drawer header (logo already present elsewhere for desktop) */}
                <div className="hidden md:block">
                  <Logo />
                </div>
                <button
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-md hover:bg-gray-100"
                >
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <nav className="px-4">
              <ul>
                <li className="mb-2">
                  <NavItem
                    link="/home"
                    icon={
                      <Layers size={20} className="size-6" />
                    }
                    menu="Task Board"
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                    onSelect={(m) => {
                      setActiveMenu(m || "/home");
                      if (disableInternalMobile && typeof onNavigate === "function") {
                        onNavigate();
                      } else {
                        setMobileOpen(false);
                      }
                    }}
                  />
                </li>
                <li className="mb-2">
                  <NavItem
                    link="#logout"
                    icon={
                      <LogOut size={18} className="size-6" />
                    }
                    activeMenu={activeMenu}
                    setActiveMenu={setActiveMenu}
                    onSelect={(m) => {
                      setActiveMenu(m || "/home");
                      if (disableInternalMobile && typeof onNavigate === "function")
                        onNavigate();
                      else setMobileOpen(false);
                      logout();
                    }}
                    menu="Log out"
                  />
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default VerticalNavbar;